import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { navIds } from "../../data/content";
import { useContent } from "../../i18n/useContent";
import Button from "../ui/Button";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import Icon from "../ui/Icons";
import { useRoute, useSectionHref } from "../../seo/RouteContext";
import { PAGE_LINKS } from "../../seo/pageLinks";
import logoBlack from "../../assets/brand/cgmg-logo-black.png";
import "./Navbar.css";

export function Navbar() {
  const { nav, site, hero } = useContent();
  const { t } = useTranslation();
  const { isHome } = useRoute();
  const sectionHref = useSectionHref();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Section highlighting only makes sense on the home page.
  const [active, setActive] = useState(isHome ? "inicio" : null);
  const tickingRef = useRef(false);

  useEffect(() => {
    function onScroll() {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        tickingRef.current = false;
      });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isHome || typeof IntersectionObserver === "undefined") return undefined;
    const sections = navIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHome]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className={`navbar${scrolled ? " is-scrolled" : ""}${open ? " is-open" : ""}`}>
        <div className="container navbar-inner">
          <a href={isHome ? "#inicio" : "/"} className="navbar-brand" onClick={() => setOpen(false)}>
            <img src={logoBlack} alt={site.name} />
          </a>

          <nav className="navbar-links" aria-label={t("a11y.mainNav")}>
            {nav.map((item) => {
              const subs = PAGE_LINKS.filter((l) => l.section === item.id);
              const link = (
                <a
                  key={item.id}
                  href={sectionHref(item.id)}
                  className={active === item.id ? "is-active" : ""}
                >
                  {item.label}
                </a>
              );
              if (!subs.length) return link;
              // Hover / keyboard-focus dropdown with the related city and
              // service pages; the section link itself works as before.
              return (
                <div key={item.id} className="navbar-item has-sub">
                  {link}
                  <div className="navbar-sub">
                    {subs.map((l) => (
                      <a key={l.id} href={l.href}>
                        {t(`nav.sub.${l.id}`)}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

          {isHome && <LanguageSwitcher className="navbar-lang" />}

          <div className="navbar-cta">
            <Button href={sectionHref("contacto")} variant="primary" size="sm">
              {hero.ctaPrimary}
            </Button>
          </div>

          <button
            type="button"
            className="navbar-toggle"
            aria-label={open ? t("a11y.closeMenu") : t("a11y.openMenu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name={open ? "close" : "menu"} size={24} />
          </button>
        </div>
      </header>

      {/* Rendered as a sibling of <header>, not a child: the header's
          backdrop-filter creates a new containing block for fixed-position
          descendants, which would trap this full-screen panel inside the
          header's own 72px box instead of the viewport. */}
      <div className="navbar-mobile" role="dialog" aria-modal="true" hidden={!open}>
        <nav className="navbar-mobile-links" aria-label={t("a11y.mobileNav")}>
          {nav.map((item) => (
            <div key={item.id} className="navbar-mobile-group">
              <a href={sectionHref(item.id)} onClick={() => setOpen(false)}>
                {item.label}
              </a>
              {PAGE_LINKS.filter((l) => l.section === item.id).map((l) => (
                <a key={l.id} href={l.href} className="navbar-mobile-sub" onClick={() => setOpen(false)}>
                  {t(`nav.sub.${l.id}`)}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <Button href={sectionHref("contacto")} variant="primary" onClick={() => setOpen(false)}>
          {hero.ctaPrimary}
        </Button>
      </div>
    </>
  );
}

export default Navbar;
